/**
 * Global Patient Packages — cross-patient read queries (PRD.263).
 *
 * Powers the global operations page. `qPatientPackagesGlobal` returns every
 * (non-deleted) patient package for the org with its computed financials,
 * remaining-benefit rollup, last redemption, and a pending-fulfillment flag.
 * The page filters/sorts these client-side via the pure helpers in
 * `patient-packages-global-logic` (all synced data is already local).
 *
 * `has_pending_release` reuses the existing Pharmacy / Product Release
 * eligibility (a paid redemption invoice line for a medicine/product child that
 * isn't fully dispensed/released) — no new fulfillment concept is introduced.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

export interface GlobalPatientPackageRow {
  patient_package_id: string;
  patient_id: string | null;
  patient_name: string | null;
  patient_number: string | null;
  package_name: string | null;
  package_code: string | null;
  payment_status: string | null;
  redemption_status: string | null;
  grand_cents: number | null;
  paid_cents: number | null;
  balance_cents: number | null;
  valid_until: string | null;
  remaining_quantity: number | null;
  total_quantity: number | null;
  benefit_count: number | null;
  last_redeemed_at: string | null;
  has_pending_release: number | null; // 0/1
  updated_at: string | null;
}

/** All patient packages for an org with financials, balances, and pending-release flag. */
export function qPatientPackagesGlobal(orgId: string): QuerySpec<[string]> {
  return {
    key: ['patient_packages', 'global', orgId] as const,
    sql: `
      SELECT
        pp.id                         AS patient_package_id,
        pp.patient_id                 AS patient_id,
        p.full_name                   AS patient_name,
        p.patient_number              AS patient_number,
        pp.package_name_snapshot      AS package_name,
        pp.package_code_snapshot      AS package_code,
        pp.payment_status             AS payment_status,
        pp.redemption_status          AS redemption_status,
        pp.valid_until                AS valid_until,
        pp.updated_at                 AS updated_at,
        CAST(ROUND(COALESCE(i.grand_total, 0) * 100) AS INTEGER) AS grand_cents,
        COALESCE(paid.paid_cents, 0)  AS paid_cents,
        CAST(ROUND(COALESCE(i.grand_total, 0) * 100) AS INTEGER) - COALESCE(paid.paid_cents, 0)
                                      AS balance_cents,
        COALESCE(items.remaining_q, 0) AS remaining_quantity,
        COALESCE(items.total_q, 0)     AS total_quantity,
        COALESCE(items.benefit_count, 0) AS benefit_count,
        red.last_redeemed_at          AS last_redeemed_at,
        CASE WHEN EXISTS (
          SELECT 1
          FROM patient_package_redemptions r
          JOIN invoice_items ii ON ii.id = r.source_invoice_item_id
          JOIN inventory_items inv ON inv.id = ii.inventory_item_id
          WHERE r.patient_package_id = pp.id
            AND r.status = 'posted'
            AND r.deleted_at IS NULL
            AND ii.inventory_item_id IS NOT NULL
            AND (inv.item_type = 'medicine' OR inv.item_type = 'product')
            AND COALESCE(ii.qty, 0)
                - COALESCE((SELECT SUM(COALESCE(pdl.quantity_dispensed, 0))
                            FROM pharmacy_dispense_lines pdl
                            WHERE pdl.invoice_item_id = ii.id AND pdl.status = 'confirmed'), 0)
                - COALESCE((SELECT SUM(COALESCE(prl.quantity_released, 0))
                            FROM product_release_lines prl
                            WHERE prl.invoice_item_id = ii.id AND prl.status = 'confirmed'), 0)
                > 0
        ) THEN 1 ELSE 0 END AS has_pending_release
      FROM patient_packages pp
      LEFT JOIN patients p ON p.id = pp.patient_id
      LEFT JOIN invoices i ON i.id = pp.source_invoice_id
      LEFT JOIN (
        SELECT invoice_id, COALESCE(SUM(amount_cents), 0) AS paid_cents
        FROM invoice_payments
        WHERE invoice_id IS NOT NULL
        GROUP BY invoice_id
      ) paid ON paid.invoice_id = pp.source_invoice_id
      LEFT JOIN (
        SELECT patient_package_id,
               SUM(COALESCE(remaining_quantity, 0)) AS remaining_q,
               SUM(COALESCE(total_quantity, 0))     AS total_q,
               COUNT(*)                             AS benefit_count
        FROM patient_package_items
        WHERE deleted_at IS NULL
        GROUP BY patient_package_id
      ) items ON items.patient_package_id = pp.id
      LEFT JOIN (
        SELECT patient_package_id, MAX(redeemed_at) AS last_redeemed_at
        FROM patient_package_redemptions
        WHERE status = 'posted' AND deleted_at IS NULL
        GROUP BY patient_package_id
      ) red ON red.patient_package_id = pp.id
      WHERE pp.org_id = ?
        AND pp.deleted_at IS NULL
      ORDER BY pp.updated_at DESC
    `,
    params: [orgId],
    watch: [
      'patient_packages',
      'patient_package_items',
      'patient_package_redemptions',
      'patients',
      'invoices',
      'invoice_payments',
      'invoice_items',
      'inventory_items',
      'pharmacy_dispense_lines',
      'product_release_lines',
    ],
  };
}

/** Count of posted redemptions on a given `YYYY-MM-DD` (Redeemed Today card). */
export function qRedeemedTodayCount(today: string): QuerySpec<[string]> {
  return {
    key: ['patient_package_redemptions', 'redeemed-today', today] as const,
    sql: `
      SELECT COUNT(*) AS cnt
      FROM patient_package_redemptions
      WHERE status = 'posted'
        AND deleted_at IS NULL
        AND substr(redeemed_at, 1, 10) = ?
    `,
    params: [today],
    watch: ['patient_package_redemptions'],
  };
}
